import { useDeferredValue, useRef, useState } from 'react';
import { DefaultValues } from 'react-hook-form';
import { FormContainer, useForm } from 'react-hook-form-mui';

import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, Paper, Stack } from '@mui/material';
import { EGlobalSearchFields, ESortOrder, ILoanResponse } from '@patient-fi/fed-types';

import { usePageableCollection, useSortableCollection } from '../../../../hooks';
import useLoans from '../../../../services/queries/useLoans';
import { getFiltersValues } from '../../../Filters/utils';
import { Input, Popper } from '../../../index';
import LoansList from './components/BorrowerList';
import Filters from './components/Filters';
import Tabs from './components/Tabs';

function GlobalSearch() {
  const formContext = useForm<{ search: string }>({
    defaultValues: {
      search: '',
    },
  });

  const { watch, reset } = formContext;

  const search = watch('search');

  const searchValue = useDeferredValue(search);

  const [currentTab, setCurrentTab] = useState<EGlobalSearchFields | null>(null);
  const gridProps = usePageableCollection();
  const { sortModel, onSortModelChange } = useSortableCollection<ILoanResponse>();
  const [filtersValues, setFiltersValues] = useState<DefaultValues<Record<string, unknown>> | undefined>();

  const { data, isFetched } = useLoans(
    {
      searchParams: {
        search: searchValue,
        searchParams: currentTab ? [currentTab] : [],
        sortField: (sortModel?.[0]?.field as keyof ILoanResponse) ?? 'fullNameCustomer',
        sortOrder: sortModel?.[0]?.sort ? (sortModel[0].sort === 'desc' ? -1 : 1) : ESortOrder.ASC,
        page: gridProps.currentPage + 1,
        limit: gridProps.pageSize,
        ...filtersValues,
      },
    },
    { enabled: Boolean(searchValue) && searchValue.trim().length >= 3 },
  );

  const ref = useRef();

  const onFiltersSubmit = (data: Record<string, unknown>) => {
    setFiltersValues(getFiltersValues(data));
  };

  const onTabChange = (value: EGlobalSearchFields | null) => {
    setCurrentTab(value);
  };

  return (
    <>
      <FormContainer formContext={formContext}>
        <Input
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
            ref: ref,
          }}
          name="search"
          label="Search"
        />
      </FormContainer>
      <Popper
        sx={{ width: 1 / 2, maxHeight: 3 / 4, overflow: 'auto' }}
        open={Boolean(searchValue)}
        anchorEl={ref.current}
        placement="bottom"
      >
        <Paper elevation={6} sx={{ p: 3, mt: 2 }}>
          <Stack spacing={2}>
            <Stack direction="row-reverse">
              <IconButton onClick={() => reset()}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Filters isLoading={false} onSubmit={onFiltersSubmit} />
            <Tabs value={currentTab} onChange={onTabChange} />
            {isFetched && <LoansList data={data?.items!} gridProps={{ sortModel, onSortModelChange, ...gridProps }} />}
          </Stack>
        </Paper>
      </Popper>
    </>
  );
}

export default GlobalSearch;
